import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import {
  AwardChip,
  ConfirmDialog,
  DataTable,
  InlineAlert,
  Loader,
  LoadingButton,
  ScoreCell,
} from "src/grading/components";
import { AppMenu } from "src/grading/components";
import type { Award } from "src/grading/components/AwardChip/AwardChip";
import EditScoreDrawer, {
  BenchmarkLookup,
  formatScore,
} from "src/grading/components/EditScoreDrawer/EditScoreDrawer";
import RevealOverlay from "src/grading/components/RevealOverlay/RevealOverlay";
import type { ScoreTone } from "src/grading/components/ScoreCell/ScoreCell";
import StudentCell from "src/grading/components/StudentCell/StudentCell";
import StudentRecordDrawer, {
  StudentRecordStudent,
} from "src/grading/components/StudentRecordDrawer/StudentRecordDrawer";
import TallyRow from "src/grading/components/TallyRow/TallyRow";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { PageHeader } from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { useSessionSocket } from "src/hooks/useSessionSocket";
import type { ApiError } from "src/models";
import {
  CATEGORY_EVENTS,
  Category,
  CategoryResult,
  EVENT_LABEL,
  EventId,
  SessionAction,
  SessionEntry,
  StudentResult,
  StudentStatus,
} from "src/models/sessions";
import { useAppDispatch } from "src/store/hooks";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  useDeleteEntryMutation,
  useGetBenchmarksQuery,
  useGetReviewQuery,
  useReassignEntryMutation,
  useSetStudentStatusMutation,
  useTransitionSessionMutation,
} from "src/store/reducers/sessions/sessionsApiSlice";
import AuditDrawer from "./AuditDrawer";
import FlagsPanel from "./FlagsPanel";

const CATEGORIES: Category[] = ["core", "cardio", "upper"];
/** An entry received this recently gets the ScoreCell "just arrived" highlight. */
const FRESH_MS = 8_000;

const messageOf = (e: unknown, fallback: string) => {
  const message = (e as ApiError | undefined)?.data?.message;
  return Array.isArray(message) ? message.join(", ") : (message ?? fallback);
};

/** Tone of one category score: tier met, excluded (adaptation or alternative), modified, or waiting. */
const toneOf = (r: CategoryResult | undefined): ScoreTone => {
  if (!r) return "w";
  if (
    r.excluded ||
    r.modification === "adaptation" ||
    r.modification === "alternative"
  )
    return "x";
  if (r.modification === "modification") return "m";
  if (r.presidentialMet) return "p";
  if (r.nationalMet) return "n";
  return "b";
};

const awardOf = (s: StudentResult): Award =>
  s.status === "absent" || s.status === "excused" ? s.status : s.awardTier;

interface Edit {
  student: StudentResult;
  category: Category;
  eventId: EventId;
  entry: SessionEntry | null;
}

type RevealCounts = {
  presidential: number;
  national: number;
  participation: number;
};

interface EntryAction {
  student: StudentResult;
  category: Category;
  entry: SessionEntry;
  reassignTo: string;
}

/** Live class grid with score edit, absent/excused, flags, reassignment, finalize and reopen. */
const SessionReviewPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data, isLoading, isError, refetch } = useGetReviewQuery(sessionId);
  const benchmarkVersion = data?.session.benchmarkVersion ?? undefined;
  // Pinned version when the session has one; otherwise the version in force on the test date.
  const { data: benchmarkData } = useGetBenchmarksQuery(
    benchmarkVersion
      ? { version: benchmarkVersion }
      : { date: data?.session.date },
    { skip: !data },
  );
  const [deleteEntry] = useDeleteEntryMutation();
  const [reassign] = useReassignEntryMutation();
  const [setStatus] = useSetStudentStatusMutation();
  const [transition, { isLoading: busy }] = useTransitionSessionMutation();
  const [edit, setEdit] = useState<Edit | null>(null);
  const [entryAction, setEntryAction] = useState<EntryAction | null>(null);
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    student: StudentResult;
  } | null>(null);
  const [recordFor, setRecordFor] = useState<string | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [reveal, setReveal] = useState<RevealCounts | null>(null);
  const [entryError, setEntryError] = useState<string | null>(null);
  const [entryBusy, setEntryBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const poll = useCallback(() => void refetch(), [refetch]);
  useSessionSocket(sessionId, () => void refetch(), poll);
  const canEditRecord = hasPermissions(
    permissions.UPDATE_ACCOMMODATIONS.action,
    permissions.UPDATE_ACCOMMODATIONS.subject,
  );
  const canViewHealth = hasPermissions(
    "read",
    "account-service:accommodations",
  );
  const name = data?.session.name;

  useEffect(() => {
    if (!name) return undefined;
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({ label: t("tabs.sessions"), path: "/grading/sessions" }),
    );
    dispatch(
      addBreadcrumb({ label: name, path: `/grading/sessions/${sessionId}` }),
    );
    dispatch(addBreadcrumb({ label: t("review.title") }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, name, sessionId, t]);

  const recordRoster = data?.session.rosterSnapshot.find(
    (r) => r.studentId === recordFor,
  );
  const recordSchoolId = data?.session.schoolId ?? "";
  const recordDistrictId = data?.session.districtId ?? "";
  // Memoized so live refetches (socket, poll) keep the drawer's unsaved edits.
  const recordStudent = useMemo<StudentRecordStudent | null>(
    () =>
      recordRoster
        ? {
            id: recordRoster.studentId,
            schoolId: recordSchoolId,
            districtId: recordDistrictId,
            firstName: recordRoster.firstName,
            lastName: recordRoster.lastName,
            benchmarkSex: recordRoster.benchmarkSex,
            benchmarkSexSource: recordRoster.benchmarkSexSource,
            birthDate: null,
            ageOverride: recordRoster.ageAtTest,
            gradeLevel: recordRoster.gradeLevel ?? null,
            externalId: undefined,
            source: undefined,
            active: true,
            consentFlags: { videoOptOut: false, photoOptOut: false },
            accommodation: {
              plannedModification: recordRoster.plannedModification,
              plannedEventId: recordRoster.plannedEventId,
              plannedNote: recordRoster.plannedNote,
              healthFlags: recordRoster.healthFlags ?? [],
              notes: null,
            },
          }
        : null,
    [recordRoster, recordSchoolId, recordDistrictId],
  );

  if (isError && !data)
    return (
      <InlineAlert
        severity="error"
        action={
          <Button color="inherit" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        }
      >
        {t("review.loadError", {
          defaultValue: "Could not load. Check your connection and try again.",
        })}
      </InlineAlert>
    );
  if (isLoading || !data) return <Loader />;
  const { session, students, summary, flags } = data;
  const nameOf = (studentId: string | null) => {
    const s = students.find((x) => x.studentId === studentId);
    return s
      ? `${s.lastName}, ${s.firstName}`
      : t("review.sessionRow", { defaultValue: "Session" });
  };
  const locked = session.status === "finalized";
  const entries = session.entries.filter((e) => !e.supersededBy);
  const openDuplicates = flags.some(
    (f) => f.status === "open" && f.type === "duplicate",
  );
  const now = Date.now();

  const entryFor = (student: StudentResult, category: Category) => {
    const r = student.categories[category];
    return (
      (r?.entryId
        ? entries.find((e) => e.id === r.entryId)
        : entries.find(
            (e) => e.studentId === student.studentId && e.category === category,
          )) ?? null
    );
  };

  const openEdit = (student: StudentResult, category: Category) => {
    const r = student.categories[category];
    const roster = session.rosterSnapshot.find(
      (s) => s.studentId === student.studentId,
    );
    const choice =
      roster?.eventChoices?.[category] ?? session.eventsByCategory?.[category];
    const eventId = (r?.eventId ??
      (choice && choice !== "student-choice"
        ? choice
        : CATEGORY_EVENTS[category][0])) as EventId;
    setEdit({ student, category, eventId, entry: entryFor(student, category) });
  };

  /** Session benchmark rows; before they load, the thresholds the server scored this cell with. */
  const benchmarksFor = (e: Edit): BenchmarkLookup => {
    if (benchmarkData?.rows?.length) return benchmarkData.rows;
    const r = e.student.categories[e.category];
    return (eventId: EventId) =>
      r && r.eventId === eventId
        ? {
            presidentialThreshold: r.presidentialThreshold,
            nationalThreshold: r.nationalThreshold,
            comparison: eventId === "pft-mile" ? "max" : "min",
          }
        : null;
  };

  const act = async (action: SessionAction) => {
    setError(null);
    try {
      const result = await transition({
        id: sessionId,
        action,
        reason: action === "reopen" ? reason : undefined,
      }).unwrap();
      if (action === "finalize") {
        // Final counts: from the transition response, else from the review refetched after it.
        const final =
          result?.summary ?? (await refetch())?.data?.summary ?? summary;
        setReveal({
          presidential: final.presidential,
          national: final.national,
          participation: final.participation,
        });
      }
    } catch (err) {
      setError(
        messageOf(
          err,
          t("sessions.transitionError", {
            defaultValue: "Could not change the session status.",
          }),
        ),
      );
    }
  };

  const runEntry = async (call: () => Promise<unknown>) => {
    setEntryError(null);
    setEntryBusy(true);
    try {
      await call();
      setEntryAction(null);
    } catch (err) {
      setEntryError(
        messageOf(
          err,
          t("review.entryActionError", {
            defaultValue: "Could not change the entry. Try again.",
          }),
        ),
      );
    } finally {
      setEntryBusy(false);
    }
  };

  const changeStatus = async (s: StudentResult, next: string) => {
    setStatusError(null);
    try {
      await setStatus({
        id: sessionId,
        studentId: s.studentId,
        status: next as StudentStatus,
      }).unwrap();
    } catch (err) {
      setStatusError(
        messageOf(
          err,
          t("review.statusError", {
            defaultValue: "Could not change {{name}}'s status.",
            name: s.firstName,
          }),
        ),
      );
    }
  };

  const missingCount = students.filter(
    (s) => s.status === "present" && s.missingCategories.length > 0,
  ).length;
  const finalizeHint =
    session.status === "open"
      ? t("review.hintCloseFirst", {
          defaultValue: "Close stations before finalizing.",
        })
      : openDuplicates
        ? t("review.hintDuplicates", {
            defaultValue: "Keep one entry for each duplicate to finalize.",
          })
        : missingCount > 0
          ? `${t("review.hintMissing", {
              defaultValue: "{{count}} students have a missing event.",
              count: missingCount,
            })} ${t("review.finalizeIncompleteNote")}`
          : t("review.hintReady", { defaultValue: "Ready to finalize." });

  const statusOptions = [
    { value: "present", label: t("review.present") },
    { value: "absent", label: t("review.absent") },
    { value: "excused", label: t("review.excused") },
    {
      value: "makeup_pending",
      label: t("review.statusMakeUp", { defaultValue: "Make-up" }),
    },
  ];

  const cell = (s: StudentResult, c: Category) => {
    const absent = s.status === "absent" || s.status === "excused";
    const r = s.categories[c];
    if (absent && !r) {
      return (
        <Typography component="span" color="text.secondary">
          —
        </Typography>
      );
    }
    const entry = entryFor(s, c);
    const arrived = entry
      ? Date.parse(entry.receivedAt ?? entry.recordedAt)
      : 0;
    const hint = r
      ? [
          r.modification !== "none"
            ? t(`modification.short.${r.modification}`, {
                defaultValue: r.modification,
              })
            : null,
          !r.excluded && r.nextTierGap !== null
            ? t(
                r.eventId === "pft-mile"
                  ? "results.nextBenchmarkTime"
                  : "results.nextBenchmark",
                {
                  gap: r.nextTierGap,
                  tier: r.nationalMet
                    ? t("results.tier.presidential")
                    : t("results.tier.national"),
                },
              )
            : null,
          r.alternateThresholds
            ? `${r.alternateThresholds.sex}: ${r.alternateThresholds.nationalThreshold}/${r.alternateThresholds.presidentialThreshold}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : t("review.missingCategory");
    return (
      <ScoreCell
        tone={toneOf(r)}
        studentName={`${s.firstName} ${s.lastName}`}
        value={
          r
            ? `${formatScore(r.eventId, r.value)}${r.mode ? ` ${r.mode}` : ""}`
            : "—"
        }
        eventShort={
          r
            ? t(`events.${r.eventId}`, {
                defaultValue: EVENT_LABEL[r.eventId as EventId] ?? r.eventId,
              })
            : t(`categories.${c}`)
        }
        hint={hint || undefined}
        fresh={arrived > 0 && now - arrived < FRESH_MS}
        onClick={locked ? undefined : () => openEdit(s, c)}
      />
    );
  };

  const canFinalize = session.status === "review" && !openDuplicates;
  const banner = (text: string, action: ReactNode, tone: "info" | "muted") => (
    <Box
      sx={(theme) => ({
        display: "flex",
        gap: 1.75,
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        p: "14px 18px",
        borderRadius: `${radius.panel}px`,
        bgcolor:
          tone === "info"
            ? theme.palette.tokens.primarySoft
            : theme.palette.surfaceAlt,
      })}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: "1 1 320px" }}>
        {text}
      </Typography>
      {action}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
      <PageHeader
        title={`${t("review.title")} · ${session.name}`}
        actions={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", md: "flex-end" },
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
              <AuditDrawer
                sessionId={sessionId}
                nameOf={nameOf}
                open={auditOpen}
                onOpen={() => setAuditOpen(true)}
                onClose={() => setAuditOpen(false)}
              />
              {locked ? (
                <Button
                  variant="contained"
                  onClick={() =>
                    history.push(`/grading/sessions/${sessionId}/certificates`)
                  }
                >
                  {t("sessions.certificatesAndLetters", {
                    defaultValue: "Certificates & letters →",
                  })}
                </Button>
              ) : (
                <LoadingButton
                  variant="contained"
                  label={t("review.finalizeAwards", {
                    defaultValue: "Finalize awards",
                  })}
                  loading={busy}
                  disabled={!canFinalize}
                  onClick={() => act("finalize")}
                />
              )}
            </Box>
            {!locked ? (
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}
              >
                {finalizeHint}
              </Typography>
            ) : null}
          </Box>
        }
      >
        <Box sx={{ mt: 0.5 }}>
          <TallyRow
            counts={{
              presidential: summary.presidential,
              national: summary.national,
              participation: summary.participation,
              incomplete: summary.incomplete,
              pending: students.filter((s) => s.status === "makeup_pending")
                .length,
            }}
          />
        </Box>
      </PageHeader>

      {session.status === "open"
        ? banner(
            t("review.stationsStillOpen", {
              defaultValue:
                "Stations are still open, so scores keep arriving here live. Close stations when every group has finished.",
            }),
            <LoadingButton
              variant="contained"
              color="secondary"
              size="small"
              label={t("sessions.closeStations")}
              loading={busy}
              onClick={() => act("close_stations")}
            />,
            "info",
          )
        : null}
      {locked
        ? banner(
            t("review.awardsLockedReopen", {
              defaultValue:
                "Awards are locked. Reopening is logged, and any certificate whose award changes is marked superseded.",
            }),
            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                size="small"
                label={t("review.reopenReason")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                inputProps={{ maxLength: 500 }}
                sx={{ minWidth: 240 }}
              />
              <LoadingButton
                variant="outlined"
                color="error"
                size="small"
                label={t("review.reopen")}
                loading={busy}
                disabled={!reason.trim()}
                onClick={() => act("reopen")}
              />
            </Box>,
            "muted",
          )
        : null}
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

      <FlagsPanel
        session={session}
        students={students}
        flags={flags}
        locked={locked}
        onEditEntry={(studentId, entryId) => {
          const s = students.find((x) => x.studentId === studentId);
          const e = session.entries.find((x) => x.id === entryId);
          if (s && e?.category) openEdit(s, e.category);
        }}
      />

      <Paper component="section" sx={{ overflow: "hidden", minWidth: 0 }}>
        {statusError ? (
          <Box sx={{ p: 2 }}>
            <InlineAlert severity="error">{statusError}</InlineAlert>
          </Box>
        ) : null}
        <DataTable<StudentResult>
          aria-label={t("console.classGrid")}
          dense
          minWidth={880}
          rows={students}
          getRowKey={(s) => s.studentId}
          columns={[
            {
              key: "student",
              header: t("review.student"),
              render: (s) => (
                <StudentCell
                  firstName={s.firstName}
                  lastName={s.lastName}
                  onClick={() => setRecordFor(s.studentId)}
                  meta={[
                    s.benchmarkSex ?? "—",
                    t("review.ageLabel", {
                      defaultValue: "age {{age}}",
                      age: s.ageAtTest ?? "—",
                    }),
                    s.benchmarkSexSource === "school"
                      ? t("review.bothTablesShown", {
                          defaultValue: "both tables shown",
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ),
            },
            ...CATEGORIES.map((c) => ({
              key: c,
              header: t(`categories.${c}`),
              render: (s: StudentResult) => cell(s, c),
            })),
            {
              key: "award",
              header: t("review.award"),
              render: (s) => <AwardChip award={awardOf(s)} size="sm" />,
            },
            {
              key: "status",
              header: t("review.status"),
              width: 170,
              render: (s) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TextField
                    select
                    size="small"
                    value={s.status}
                    disabled={locked}
                    onChange={(e) => changeStatus(s, e.target.value)}
                    SelectProps={{
                      SelectDisplayProps: {
                        "aria-label": t("review.statusFor", {
                          defaultValue: "{{name}} status",
                          name: s.firstName,
                        }),
                      },
                    }}
                    sx={{
                      minWidth: 124,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontSize: 13,
                        fontWeight: 600,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider",
                      },
                    }}
                  >
                    {statusOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {!locked &&
                  CATEGORIES.some((c) => entryFor(s, c) !== null) ? (
                    <IconButton
                      size="small"
                      aria-label={t("review.entryActionsFor", {
                        defaultValue: "Move or delete an entry for {{name}}",
                        name: s.firstName,
                      })}
                      aria-haspopup="menu"
                      aria-controls={
                        menu?.student.studentId === s.studentId
                          ? "entry-actions-menu"
                          : undefined
                      }
                      aria-expanded={
                        menu?.student.studentId === s.studentId
                          ? "true"
                          : undefined
                      }
                      onClick={(e) =>
                        setMenu({ anchor: e.currentTarget, student: s })
                      }
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              ),
            },
          ]}
        />
        <Typography
          sx={(theme) => ({
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${theme.palette.divider}`,
            fontSize: 13,
            fontWeight: 600,
            color: "text.secondary",
          })}
        >
          {t("review.gridFooter", {
            defaultValue:
              "Click any score to edit it. Every edit is logged with who, when, and the old and new value.",
          })}
        </Typography>
      </Paper>

      <AppMenu
        id="entry-actions-menu"
        anchorEl={menu?.anchor ?? null}
        open={!!menu}
        onClose={() => setMenu(null)}
      >
        {menu
          ? CATEGORIES.map((c) => {
              const entry = entryFor(menu.student, c);
              return entry ? (
                <MenuItem
                  key={c}
                  onClick={() => {
                    setEntryError(null);
                    setEntryAction({
                      student: menu.student,
                      category: c,
                      entry,
                      reassignTo: "",
                    });
                    setMenu(null);
                  }}
                >
                  {`${t(`categories.${c}`)} · ${t(`events.${entry.eventId}`, {
                    defaultValue:
                      EVENT_LABEL[entry.eventId as EventId] ?? entry.eventId,
                  })} ${formatScore(String(entry.eventId), Number(entry.value))}`}
                </MenuItem>
              ) : null;
            })
          : null}
      </AppMenu>

      <Dialog
        open={!!entryAction}
        onClose={() => setEntryAction(null)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="entry-action-title"
      >
        {entryAction ? (
          <>
            <DialogTitle id="entry-action-title">
              {entryAction.student.firstName} {entryAction.student.lastName} ·{" "}
              {EVENT_LABEL[entryAction.entry.eventId as EventId] ??
                entryAction.entry.eventId}
            </DialogTitle>
            <DialogContent>
              <TextField
                select
                fullWidth
                label={t("station.wrongStudent")}
                value={entryAction.reassignTo}
                onChange={(e) =>
                  setEntryAction({ ...entryAction, reassignTo: e.target.value })
                }
                helperText={t("review.moveEntryHelp", {
                  defaultValue: "Move this entry to another student",
                })}
              >
                <MenuItem value="">—</MenuItem>
                {session.rosterSnapshot
                  .filter((r) => r.studentId !== entryAction.student.studentId)
                  .map((r) => (
                    <MenuItem key={r.studentId} value={r.studentId}>
                      {r.lastName}, {r.firstName}
                    </MenuItem>
                  ))}
              </TextField>
              {entryError ? (
                <Box sx={{ mt: 2 }}>
                  <InlineAlert severity="error">{entryError}</InlineAlert>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={() => setConfirmDelete(true)}>
                {t("review.deleteEntry")}
              </Button>
              <Button onClick={() => setEntryAction(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={!entryAction.reassignTo || entryBusy}
                onClick={() =>
                  runEntry(() =>
                    reassign({
                      sessionId,
                      entryId: entryAction.entry.id,
                      studentId: entryAction.reassignTo,
                    }).unwrap(),
                  )
                }
              >
                {t("review.reassign", { defaultValue: "Reassign" })}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete && !!entryAction}
        tone="destructive"
        title={t("review.deleteEntryTitle", {
          defaultValue: "Delete this entry?",
        })}
        body={t("review.deleteEntryBody", {
          defaultValue:
            "The score is removed from the grid. The deletion is logged in the audit log.",
        })}
        confirmLabel={t("review.deleteEntry")}
        loading={entryBusy}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (!entryAction) return;
          setConfirmDelete(false);
          void runEntry(() =>
            deleteEntry({ sessionId, entryId: entryAction.entry.id }).unwrap(),
          );
        }}
      />

      {edit ? (
        <EditScoreDrawer
          open
          onClose={() => setEdit(null)}
          sessionId={sessionId}
          entry={edit.entry}
          student={{
            id: edit.student.studentId,
            firstName: edit.student.firstName,
            lastName: edit.student.lastName,
            age: edit.student.ageAtTest,
            benchmarkSex: edit.student.benchmarkSex,
          }}
          category={edit.category}
          defaultEventId={edit.eventId}
          benchmarks={benchmarksFor(edit)}
        />
      ) : null}

      <StudentRecordDrawer
        open={!!recordStudent}
        onClose={() => setRecordFor(null)}
        student={recordStudent}
        classLabel={session.name}
        classId={session.classId ?? undefined}
        ageOnTestDay={recordRoster?.ageAtTest ?? null}
        testDate={session.date}
        canEdit={canEditRecord}
        canViewHealth={canViewHealth}
      />

      <RevealOverlay
        open={!!reveal}
        className={session.name}
        benchmarkVersion={session.benchmarkVersion ?? 1}
        counts={
          reveal ?? {
            presidential: summary.presidential,
            national: summary.national,
            participation: summary.participation,
          }
        }
        onPrint={() =>
          history.push(`/grading/sessions/${sessionId}/certificates`)
        }
        onClose={() => setReveal(null)}
      />
    </Box>
  );
};

export default SessionReviewPage;
